import React, { useState, useEffect } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface RecurringEventActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scope: 'single' | 'following' | 'all') => void | Promise<void>;
  actionType: 'edit' | 'delete' | 'move';
}

export const RecurringEventActionModal: React.FC<RecurringEventActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
}) => {
  const [selectedScope, setSelectedScope] = useState<'single' | 'following' | 'all'>('single');
  const [isProcessing, setIsProcessing] = useState(false);

  useEscapeKey(onClose, isOpen);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📖 [RecurringEventActionModal] Modal opened, actionType:', actionType);
      setSelectedScope('single');
      setIsProcessing(false);
    }
  }, [isOpen, actionType]);

  if (!isOpen) {
    return null;
  }

  const getTitle = () => {
    switch (actionType) {
      case 'edit':
        return 'Edit recurring event';
      case 'delete':
        return 'Delete recurring event';
      case 'move':
        return 'Edit recurring event';
    }
  };

  const handleConfirm = async () => {
    console.log('🎯 [RecurringEventActionModal] Confirm clicked with scope:', selectedScope);
    setIsProcessing(true);
    try {
      await onConfirm(selectedScope);
      console.log('✅ [RecurringEventActionModal] onConfirm completed successfully');
    } catch (error) {
      console.error('❌ [RecurringEventActionModal] Error in handleConfirm:', error);
      // Error is already handled by the parent component (WorkScheduleEditModal)
      // So we just log it here and let the modal close
    } finally {
      setIsProcessing(false);
      console.log('🏁 [RecurringEventActionModal] Processing complete, setting isProcessing to false');
    }
  };

  console.log('🎨 [RecurringEventActionModal] Rendering with isOpen:', isOpen, 'isProcessing:', isProcessing, 'selectedScope:', selectedScope);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onClick={(e) => {
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget && !isProcessing) {
          console.log('🚪 [RecurringEventActionModal] Backdrop clicked, closing modal');
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 relative z-[61]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{getTitle()}</h2>
        
        <div className="space-y-3 mb-6">
          {/* This event */}
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="single"
              checked={selectedScope === 'single'}
              onChange={() => {
                console.log('🔘 [RecurringEventActionModal] Selected: single');
                setSelectedScope('single');
              }}
              className="w-5 h-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-900">This event</span>
          </label>

          {/* This and following events */}
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="following"
              checked={selectedScope === 'following'}
              onChange={() => {
                console.log('🔘 [RecurringEventActionModal] Selected: following');
                setSelectedScope('following');
              }}
              className="w-5 h-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-900">This and following events</span>
          </label>

          {/* All events */}
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="scope"
              value="all"
              checked={selectedScope === 'all'}
              onChange={() => {
                console.log('🔘 [RecurringEventActionModal] Selected: all');
                setSelectedScope('all');
              }}
              className="w-5 h-5 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-gray-900">All events</span>
          </label>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              console.log('❌ [RecurringEventActionModal] Cancel button clicked');
              onClose();
            }}
            disabled={isProcessing}
            className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              console.log('✅ [RecurringEventActionModal] OK button clicked');
              handleConfirm();
            }}
            disabled={isProcessing}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Processing...' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

